import { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext();
const roles = {
  ADMIN: "Admin",
  BUYER: "Buyer",
  SELLER: "Seller",
  SECURITY: "Security",
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [role, setRole] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);

  const loadUserDetailsFromStorage = () => {
    try {
      const userData = JSON.parse(sessionStorage.getItem("userData"));
      if (userData?.userDetails) {
        setUserDetails(userData.userDetails);
        // Separate profile photo logic
        if (userData.userDetails.profilePhotoUrl) {
          setProfilePhotoUrl(userData.userDetails.profilePhotoUrl);
        }
        return userData.userDetails;
      }
    } catch (error) {
      console.error("Error loading user details from sessionStorage:", error);
    }
    return null;
  };

  const userOrgId = userDetails?.organizationId;
  const userId = userDetails?.userId;
  const sellerOrganizationType = userDetails?.organizationTypeName01;

  // useEffect(() => {
  //   const userData = JSON.parse(sessionStorage.getItem("userData"));
  //   if (!userData) {
  //     window.location.href = "/login";
  //   }
  // }, []);

  const updateUserDetails = (updatedDetails) => {
    const userData = JSON.parse(sessionStorage.getItem("userData")) || {};
    const newUserDetails = { ...userDetails, ...updatedDetails };

    userData.userDetails = newUserDetails;
    sessionStorage.setItem("userData", JSON.stringify(userData));

    setUserDetails(newUserDetails);

    // Update profile photo if it's in the updated details
    if (updatedDetails.profilePhotoUrl !== undefined) {
      setProfilePhotoUrl(updatedDetails.profilePhotoUrl);
    }
  };

  // Load user details on mount and when sessionStorage changes
  useEffect(() => {
    const storedAuth = sessionStorage.getItem("isAuthenticated");
    const storedRole = sessionStorage.getItem("role");

    if (storedAuth === "true" && storedRole) {
      setIsAuthenticated(true);
      setRole(storedRole);
      // Load user details from sessionStorage
      loadUserDetailsFromStorage();
    } else {
      setIsAuthenticated(false);
      setRole(null);
      setUserDetails(null);
      setProfilePhotoUrl(null);
    }
    setAuthLoading(false);
  }, []);

  // Listen for storage changes (useful for multiple tabs)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "userData") {
        loadUserDetailsFromStorage();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const login = (role) => {
    setIsAuthenticated(true);
    setRole(role);
    sessionStorage.setItem("isAuthenticated", "true");
    sessionStorage.setItem("role", role);
  };

  // Function to update user details immediately (called after login success)
  const setUserDataFromLogin = (userData) => {
    if (userData?.userDetails) {
      setUserDetails(userData.userDetails);
      if (userData.userDetails.profilePhotoUrl) {
        setProfilePhotoUrl(userData.userDetails.profilePhotoUrl);
      }
    }
  };

  const logout = () => {
    console.log("Logging out...");
    sessionStorage.removeItem("isAuthenticated");
    sessionStorage.removeItem("role");
    sessionStorage.removeItem("userData");
    sessionStorage.removeItem("loginPopupDismissed");
    setIsAuthenticated(false);
    setRole(null);
    setUserDetails(null);
    setProfilePhotoUrl(null);
    if (role == "Admin") {
      window.location.href = "/admin-login";
    } else {
      window.location.href = "/login";
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        role,
        setRole,
        login,
        logout,
        userDetails,
        updateUserDetails,
        loadUserDetailsFromStorage,
        setUserDataFromLogin,
        userOrgId,
        userId,
        sellerOrganizationType,
        profilePhotoUrl,
        setProfilePhotoUrl,
        authLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export { roles };
